import { Typography } from '@euler/app/components/typography';
import {
  apdmSendingPolicyText,
  kApdmCampaignKindLabels,
} from '@euler/functions/apdm/campaignLabels';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmBuildStep,
  ApdmCampaignDailyWindow,
  ApdmCampaignKind,
  ApdmSendingPolicy,
  apdmDailyWindowText,
  kApdmBuildSteps,
  kApdmDefaultDailyCap,
  parseApdmPlanStepWidget,
} from '@euler/model/apdm';
import dayjs from 'dayjs';
import { CalendarDays } from 'lucide-react-native';
import { memo, useContext, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  AiChatPlanContext,
  AiChatSettledContext,
  useAiChatActions,
  useAiChatMessage,
  useAiPlanStepOpen,
} from '../chat/AiChatActions';
import { ChatCard } from '../components/ChatCard';
import { PrimaryButton, SecondaryButton } from '../components/PrimaryButton';
import { aiColors, tint } from '../theme';

const kKinds: ApdmCampaignKind[] = [
  ApdmCampaignKind.WinBack,
  ApdmCampaignKind.MaintenanceDue,
  ApdmCampaignKind.Promotion,
  ApdmCampaignKind.Greeting,
];

/** A step's question with tap answers: what, or when (spec §11.4.2). */
export const PlanStepCard = memo(
  ({ data, streaming }: { data: unknown; streaming?: boolean }) => {
    const widget = parseApdmPlanStepWidget(data);
    const { latest } = useAiChatMessage();
    const current = useContext(AiChatPlanContext)?.currentStep;
    if (streaming || !widget) return null;
    // A question for a step the build has not reached waits for its turn:
    // the open step is settled first.
    if (
      latest &&
      current &&
      kApdmBuildSteps.indexOf(widget.step) > kApdmBuildSteps.indexOf(current)
    )
      return null;
    return widget.step === ApdmBuildStep.What ? (
      <KindPills suggested={widget.suggested} />
    ) : (
      <PeriodCard
        startDate={widget.startDate}
        endDate={widget.endDate}
        dailyCap={widget.dailyCap}
        dailyWindow={widget.dailyWindow}
        sendingPolicy={widget.sendingPolicy}
      />
    );
  },
);

function Pill({
  label,
  selected,
  disabled,
  onPress,
}: {
  label: string;
  selected?: boolean;
  disabled?: boolean;
  onPress: () => void;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: !!selected, disabled: !!disabled }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => ({
        paddingHorizontal: rpx(14),
        paddingVertical: rpx(8),
        borderRadius: rpx(18),
        borderWidth: 1,
        borderColor: selected ? aiColors.primary : aiColors.border,
        backgroundColor: selected
          ? tint(aiColors.primary, 0.08)
          : aiColors.card,
        opacity: disabled ? 0.6 : pressed ? 0.7 : 1,
      })}
    >
      <Typography.Text
        size={rpx(13)}
        color={selected ? aiColors.primary : aiColors.foreground}
      >
        {label}
      </Typography.Text>
    </Pressable>
  );
}

const KindPills = memo(
  ({ suggested }: { suggested: ApdmCampaignKind | null }) => {
    const { rpx } = useResponsiveContext();
    const chat = useAiChatActions();
    const open = useAiPlanStepOpen(ApdmBuildStep.What);
    const ordered = suggested
      ? [suggested, ...kKinds.filter(kind => kind !== suggested)]
      : kKinds;
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(8) }}>
        {ordered.map(kind => (
          <Pill
            key={kind}
            label={kApdmCampaignKindLabels[kind]}
            selected={kind === suggested}
            disabled={!open}
            onPress={() =>
              chat.send(kApdmCampaignKindLabels[kind], {
                answer: { step: ApdmBuildStep.What, kind },
              })
            }
          />
        ))}
      </View>
    );
  },
);

/** How many customers a day, as the card and the user's answer say it. */
function capText(cap: number | null): string {
  return cap == null ? '每天不限人数' : `每天最多 ${cap} 位`;
}

function day(value: string): string {
  return dayjs(value).format('M月D日');
}

const kDateFormat = 'YYYY-MM-DD';

/** The last day of a run of `amount` units starting on `start`. */
function lastDay(
  start: string,
  amount: number,
  unit: 'month' | 'week',
): string {
  return dayjs(start).add(amount, unit).subtract(1, 'day').format(kDateFormat);
}

/**
 * The proposed days, with quick alternatives; a tap settles when. Only days:
 * the daily window decides the hours, and the campaign card offers to send
 * the first messages immediately.
 */
const PeriodCard = memo(
  ({
    startDate,
    endDate,
    dailyCap,
    dailyWindow,
    sendingPolicy,
  }: {
    startDate: string;
    endDate: string | null;
    /** The planner's proposal; absent proposes the kind's default. */
    dailyCap?: number | null;
    /** The hours the user named, sent back so a tap keeps them. */
    dailyWindow?: ApdmCampaignDailyWindow;
    /** Where it reaches customers, sent back so a tap keeps it. */
    sendingPolicy?: ApdmSendingPolicy;
  }) => {
    const { rpx } = useResponsiveContext();
    const chat = useAiChatActions();
    const interactive = useAiPlanStepOpen(ApdmBuildStep.When);
    const kind = useContext(AiChatSettledContext)?.kind;
    const proposedCap =
      dailyCap !== undefined
        ? dailyCap
        : kind === ApdmCampaignKind.Greeting
        ? null
        : kApdmDefaultDailyCap;
    const [cap, setCap] = useState<number | null>(proposedCap);
    const caps = useMemo(() => {
      const values: Array<number | null> = [100, 200, 500, 1000, null];
      return values.includes(proposedCap) ? values : [proposedCap, ...values];
    }, [proposedCap]);
    const starts = useMemo(() => {
      const today = dayjs();
      return [
        { label: '建议日期', value: startDate },
        { label: '今天', value: today.format(kDateFormat) },
        { label: '明天', value: today.add(1, 'day').format(kDateFormat) },
        {
          label: '下周一',
          value: today
            .add((8 - today.day()) % 7 || 7, 'day')
            .format(kDateFormat),
        },
      ];
    }, [startDate]);
    const [start, setStart] = useState(startDate);
    const ends = useMemo(
      () =>
        [
          { label: '建议日期', value: endDate },
          { label: '1 个月', value: lastDay(start, 1, 'month') },
          { label: '2 周', value: lastDay(start, 2, 'week') },
          { label: '不设结束', value: null },
          // A suggested end before a later start cannot be chosen.
        ].filter(option => option.value == null || option.value >= start),
      [start, endDate],
    );
    const [end, setEnd] = useState<string | null>(endDate);
    // A later start can pass the chosen last day; the end then moves to a
    // month after the new start rather than before it.
    const pickStart = (value: string) => {
      setStart(value);
      if (end && end < value) setEnd(lastDay(value, 1, 'month'));
    };
    const confirm = () =>
      chat.send(
        (end ? `${day(start)}到${day(end)}` : `从${day(start)}开始，不设结束`) +
          `，${capText(cap)}` +
          (dailyWindow ? `，${apdmDailyWindowText(dailyWindow)} 发送` : '') +
          (sendingPolicy ? `，${apdmSendingPolicyText(sendingPolicy)}` : ''),
        {
          answer: {
            step: ApdmBuildStep.When,
            period: {
              startDate: start,
              endDate: end,
              dailyCap: cap,
              ...(dailyWindow ? { dailyWindow } : {}),
              ...(sendingPolicy ? { sendingPolicy } : {}),
            },
          },
        },
      );
    return (
      <ChatCard
        icon={CalendarDays}
        title="活动时间"
        subtitle={
          (end ? `${day(start)} 至 ${day(end)}` : `${day(start)} 起`) +
          ` · ${capText(cap)}` +
          (dailyWindow ? ` · ${apdmDailyWindowText(dailyWindow)}` : '') +
          (sendingPolicy ? ` · ${apdmSendingPolicyText(sendingPolicy)}` : '')
        }
      >
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          开始
        </Typography.Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(8) }}>
          {starts.map(option => (
            <Pill
              key={option.label}
              label={option.label}
              selected={option.value === start}
              disabled={!interactive}
              onPress={() => pickStart(option.value)}
            />
          ))}
        </View>
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          结束
        </Typography.Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(8) }}>
          {ends.map(option => (
            <Pill
              key={option.label}
              label={option.label}
              selected={option.value === end}
              disabled={!interactive}
              onPress={() => setEnd(option.value)}
            />
          ))}
        </View>
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          每天发送
        </Typography.Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(8) }}>
          {caps.map(option => (
            <Pill
              key={option ?? 'none'}
              label={option == null ? '不限' : `${option} 位`}
              selected={option === cap}
              disabled={!interactive}
              onPress={() => setCap(option)}
            />
          ))}
        </View>
        {interactive ? (
          <>
            <PrimaryButton title="确认时间" onPress={confirm} />
            <SecondaryButton title="换个时间" onPress={chat.focusComposer} />
          </>
        ) : null}
      </ChatCard>
    );
  },
);
